import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type CardProps = {
  appId: string;
  title: string;
  subtitle?: string;
  editing?: boolean;
  /** Rótulo acessível do lápis («Editar dados pessoais»); sem ele o card é só leitura. */
  editLabel?: string;
  editDisabled?: boolean;
  onEdit?: () => void;
  children: ReactNode;
};

/** Card da etapa «Confira seus dados» (Figma 2086:8441, edição 2088:8571 / 2088:8887). */
export function RenewalReviewCard({
  appId,
  title,
  subtitle,
  editing = false,
  editLabel,
  editDisabled,
  onEdit,
  children
}: CardProps) {
  const titleId = `${appId}-title`;
  return (
    <Box
      aria-labelledby={titleId}
      component="section"
      data-app-id={appId}
      data-editing={editing || undefined}
      role="region"
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: editing ? 'primary.light' : 'divider',
        borderRadius: '16px',
        boxShadow: editing ? (theme) => `0 4px 4px ${theme.customColors.elevation}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
        p: '16px'
      }}
    >
      <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" id={titleId} sx={{ color: 'text.primary' }} variant="h6">
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ color: 'text.secondary', display: 'block' }} variant="caption">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {editLabel && !editing ? (
          <IconButton
            aria-label={editLabel}
            data-app-id={`${appId}-edit`}
            disabled={editDisabled}
            onClick={onEdit}
            size="small"
            sx={{ color: 'text.primary' }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

/** Rótulo em `caption`/placeholder e valor em `body2` (linhas do Figma «linha-contrato»). */
export function RenewalReviewField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography sx={(theme) => ({ color: theme.customColors.textPlaceholder })} variant="caption">
        {label}
      </Typography>
      <Typography sx={{ color: 'text.primary', overflowWrap: 'anywhere' }} variant="body2">
        {value}
      </Typography>
    </Box>
  );
}

/** Aviso com cadeado: «Para alterar … fale com o atendimento». */
export function RenewalReviewLockNote({ message, divider = false }: { message: string; divider?: boolean }) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        borderTop: divider ? '1px solid' : 'none',
        borderColor: 'divider',
        display: 'flex',
        gap: '8px',
        pt: divider ? '4px' : 0
      }}
    >
      <LockOutlinedIcon aria-hidden sx={{ color: 'text.secondary', fontSize: 18, m: '7px' }} />
      <Typography sx={{ color: 'text.secondary' }} variant="caption">
        {message}
      </Typography>
    </Box>
  );
}

/** «Cancelar» e «Salvar» do card em edição. */
export function RenewalReviewEditActions({
  appId,
  saving,
  onCancel,
  onSave
}: {
  appId: string;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
      <Button data-app-id={`${appId}-cancel`} disabled={saving} onClick={onCancel} sx={{ color: 'text.primary' }}>
        Cancelar
      </Button>
      <Button
        data-app-id={`${appId}-save`}
        disabled={saving}
        onClick={onSave}
        size="small"
        startIcon={saving ? <CircularProgress aria-hidden color="inherit" size={14} /> : undefined}
        sx={{ borderRadius: '24px', px: 2 }}
        variant="contained"
      >
        Salvar
      </Button>
    </Box>
  );
}
